import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Download, RotateCcw, Award, Home, Sparkles, FileText } from "lucide-react";
import PayPalUnlockButton from "@/components/PayPalUnlockButton";
import LegalDisclaimer from "@/components/LegalDisclaimer";

export default function Result() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    const cached = sessionStorage.getItem(`exam_result_${attemptId}`);
    if (cached) {
      setResult(JSON.parse(cached));
    } else {
      navigate("/dashboard");
    }
    // attemptId + navigate are the only real deps; cached & setResult are stable.
  }, [attemptId, navigate]);

  if (!result) return null;

  const downloadCert = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificate/${result.certificate_id}/pdf`, {
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `bbp-certificate-${result.certificate_id}.pdf`;
        link.click();
      })
      .catch((err) => console.error("Certificate download failed", err));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Result header */}
        <div
          className={`rounded-lg p-10 border-2 fade-in ${
            result.passed ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-red-600"
          }`}
          data-testid="exam-result-card"
        >
          <div className="flex items-start justify-between mb-6">
            {result.passed ? (
              <Award className="h-12 w-12 text-red-500" strokeWidth={1.5} />
            ) : (
              <XCircle className="h-12 w-12 text-red-600" strokeWidth={1.5} />
            )}
            <div className="text-right">
              <div className={`text-xs font-mono uppercase tracking-widest ${result.passed ? "text-zinc-400" : "text-zinc-500"}`}>Your score</div>
              <div className="font-display text-6xl font-black leading-none mt-1" data-testid="result-score">{result.score}%</div>
              <div className={`text-sm mt-1 ${result.passed ? "text-zinc-400" : "text-zinc-600"}`}>
                {result.correct_count} of {result.total} correct
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight" data-testid="result-heading">
            {result.passed ? "You passed. Congratulations!" : "Not quite — try again."}
          </h1>
          <p className={`mt-4 max-w-xl ${result.passed ? "text-zinc-300" : "text-zinc-700"}`}>
            {result.passed
              ? "You've completed IMCAC's competency-based Bloodborne Pathogens assessment. Your independent competency certificate is valid for 2 calendar years. Download it below and keep a copy for your records."
              : "You need 80% to pass. Review the modules you're less sure about and take the assessment again — there's no limit on retries."}
          </p>

          {result.passed && result.reissued && (
            <div
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300"
              data-testid="result-reissued-badge"
            >
              <Sparkles className="h-4 w-4" />
              <span>
                New personal best — your certificate has been re-issued with your latest score of{" "}
                <strong>{result.score}%</strong>.
              </span>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {result.passed && result.certificate_id && (
              paid ? (
                <Button
                  onClick={downloadCert}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="result-download-cert-btn"
                >
                  <Download className="mr-2 h-4 w-4" /> Download certificate (PDF)
                </Button>
              ) : (
                <PayPalUnlockButton
                  certId={result.certificate_id}
                  priceLabel="$30"
                  testId="result-unlock-cert-btn"
                  onSuccess={() => { setPaid(true); downloadCert(); }}
                />
              )
            )}
            {result.passed && (
              <Button
                onClick={() => navigate("/exam")}
                variant="outline"
                className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800 hover:text-white"
                data-testid="result-retake-higher-btn"
              >
                <RotateCcw className="mr-2 h-4 w-4" /> Retake to beat your score
              </Button>
            )}
            {!result.passed && (
              <Button
                onClick={() => navigate("/exam")}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="result-retry-btn"
              >
                <RotateCcw className="mr-2 h-4 w-4" /> Retake exam
              </Button>
            )}
            <Button
              variant={result.passed ? "outline" : "outline"}
              onClick={() => navigate("/dashboard")}
              className={result.passed ? "border-zinc-700 text-white hover:bg-zinc-800 hover:text-white bg-transparent" : ""}
              data-testid="result-home-btn"
            >
              <Home className="mr-2 h-4 w-4" /> Dashboard
            </Button>
          </div>
        </div>

        {/* Score breakdown: MCQ + AI case study */}
        {(result.mcq_score !== undefined || result.case_study_score !== undefined) && (
          <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="score-breakdown">
            <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="mcq-score-card">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-zinc-700" />
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                  Multiple choice · 70% weight
                </div>
              </div>
              <div className="font-display text-4xl font-black tracking-tight">
                {result.mcq_score ?? result.score}%
              </div>
              <div className="text-sm text-zinc-600 mt-1">
                {result.correct_count} of {result.total} correct
              </div>
            </div>

            <div
              className={`rounded-lg p-6 border ${
                result.case_study_score != null ? "bg-white border-zinc-200" : "bg-zinc-50 border-zinc-200"
              }`}
              data-testid="case-study-score-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-red-600" />
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                  AI case study · 30% weight
                </div>
              </div>
              {result.case_study_score != null ? (
                <>
                  <div className="font-display text-4xl font-black tracking-tight">
                    {result.case_study_score}%
                  </div>
                  <div className="text-sm text-zinc-600 mt-1">Graded by Claude Sonnet 4.5</div>
                </>
              ) : (
                <div className="text-sm text-zinc-500 mt-2">Not submitted</div>
              )}
            </div>
          </div>
        )}

        {result.case_study_feedback && (
          <div
            className="mt-6 bg-white border-l-4 border-red-600 rounded-r-md p-6"
            data-testid="case-study-feedback"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-red-600" />
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-700">
                AI feedback on your case study
              </div>
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
              {result.case_study_feedback}
            </p>
          </div>
        )}

        {/* Review */}
        <div className="mt-12" data-testid="exam-review-section">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Review your answers</h2>
          <div className="space-y-4">
            {(result.review || []).map((r, i) => (
              <div
                key={`${r.question}-${i}`}
                className={`bg-white border rounded-lg p-6 ${r.is_correct ? "border-zinc-200" : "border-red-600"}`}
                data-testid={`review-item-${i}`}
              >
                <div className="flex items-start gap-3">
                  {r.is_correct ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Q{i + 1}</div>
                    <div className="font-medium">{r.question}</div>
                    <div className="mt-3 space-y-1.5 text-sm">
                      {r.options.map((opt, oi) => {
                        const isCorrect = oi === r.correct_index;
                        const isChosen = oi === r.chosen_index;
                        let cls = "text-zinc-600";
                        if (isCorrect) cls = "text-emerald-700 font-medium";
                        else if (isChosen && !isCorrect) cls = "text-red-700 line-through";
                        return (
                          <div key={`${r.question}-opt-${oi}`} className={cls}>
                            <span className="font-mono text-xs mr-2">{String.fromCharCode(65 + oi)}.</span>
                            {opt}
                            {isCorrect && <span className="ml-2 text-[10px] font-mono uppercase text-emerald-600">Correct</span>}
                            {isChosen && !isCorrect && <span className="ml-2 text-[10px] font-mono uppercase text-red-600">Your answer</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <LegalDisclaimer />
        </div>
      </div>
    </div>
  );
}
